import { useCanvas } from "@/hooks/useCanvas";
import { routes } from "@/routes/paths";
import { usePriorityStore } from "@/stores/priorityStore";
import { Badge, Box, Group, Text } from "@mantine/core";
import { FaArrowLeft, FaCheck, FaEye, FaStar } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { FloatingActionButton } from "@/ui";
import { useTranslation } from "react-i18next";
import { ctaCountBadgeProps, filledCtaButtonProps } from "@canucci/base-mantine/theme";

interface PrioritiesTriggerProps {
  /**
   * Callback when the trigger is clicked (only used when voting is open)
   */
  onClick: () => void;
  insightsAvailable?: boolean;
}

/**
 * Floating button that acts as a mode switcher:
 * - On insights page: shows "View canvas" to navigate back to overview
 * - On canvas pages (when voting closed): shows "View insights" to navigate to insights
 * - On canvas pages (when voting open): shows vote count and opens priorities modal
 */
function PrioritiesTrigger({ onClick, insightsAvailable = false }: PrioritiesTriggerProps) {
  const selectedCount = usePriorityStore((state) => state.getSelectedCount());
  const isSubmitted = usePriorityStore((state) => state.isSubmitted);
  const maxSelections = usePriorityStore((state) => state.maxSelections);
  const { isParticipation, externalId } = useCanvas();
  const navigate = useNavigate();
  const location = useLocation();
  const { t: tPages } = useTranslation("pages");
  // Check if we're on the insights page
  const isOnInsightsPage = location.pathname.includes("/insights");
  // On insights page: show "View canvas" button to navigate back to overview
  if (isOnInsightsPage && externalId) {
    return (
      <FloatingActionButton
        {...filledCtaButtonProps("primary")}
        ariaLabel="View canvas"
        onClick={() => {
          navigate(routes.canvas.overview.path(externalId, isParticipation), {
            viewTransition: true,
          });
        }}
      >
        <Group gap="xs">
          <FaArrowLeft size={20} />
          <Text fz="sm" fw={500}>
            {tPages("canvas.insights.modal.viewCanvas")}
          </Text>
        </Group>
      </FloatingActionButton>
    );
  }

  // On canonical Canvas pages, show results only when the API says they are available.
  if (insightsAvailable && externalId && !isParticipation) {
    return (
      <FloatingActionButton
        {...filledCtaButtonProps("primary")}
        ariaLabel="View team insights"
        onClick={() => {
          navigate(routes.canvas.insights.path(externalId, isParticipation), {
            viewTransition: true,
          });
        }}
      >
        <Group gap="xs">
          <FaEye size={20} />
          <Text fz="sm" fw={500}>
            {tPages("canvas.insights.modal.viewInsights")}
          </Text>
        </Group>
      </FloatingActionButton>
    );
  }

  // When voting is open, show the FAB with vote count
  // Show even if no votes selected yet to allow users to start voting

  // Icon and color based on state
  const IconComponent = isSubmitted ? FaCheck : FaStar;
  return (
    <FloatingActionButton
      ariaLabel={isSubmitted ? "View submitted priorities" : "View priorities"}
      onClick={onClick}
      {...filledCtaButtonProps("primary")}
    >
      <Group gap="xs">
        <Box component={IconComponent} w="20px" h="20px" />
        {selectedCount < 1 ? (
          <Text fz="sm">{tPages("canvas.insights.modal.prioritiesButton")}</Text>
        ) : isSubmitted ? (
          <Text fz="sm">{tPages("canvas.insights.modal.submittedButton")}</Text>
        ) : (
          <Badge {...ctaCountBadgeProps} radius="xl" px="xs" size="md" data-testid="priorities-trigger-count">
            {selectedCount}/{maxSelections}
          </Badge>
        )}
      </Group>
    </FloatingActionButton>
  );
}

export default PrioritiesTrigger;
